import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

export type ReportType =
  | "branch_compliance"
  | "camera_uptime"
  | "recording_availability"
  | "exception_report"
  | "sla_report"
  | "alert_summary"
  | "audit_summary";

export type ReportFormat = "pdf" | "excel" | "csv";
export type ReportFrequency = "daily" | "weekly" | "monthly" | "on_demand";
export type ReportJobStatus = "queued" | "generating" | "complete" | "failed";

export interface ReportJob {
  id: string;
  schedule_id?: string | null;
  report_type: ReportType;
  format: ReportFormat;
  status: ReportJobStatus;
  parameters: Record<string, unknown>;
  file_size_bytes?: number | null;
  error_message?: string | null;
  created_at?: unknown;
  started_at?: unknown;
  completed_at?: unknown;
  expires_at?: unknown;
  download_url?: string | null;
}

export interface ReportSchedule {
  id: string;
  name: string;
  report_type: ReportType;
  format: ReportFormat;
  frequency: ReportFrequency;
  schedule_day?: number | null;
  schedule_hour: number;
  scope_type: "all" | "entity";
  scope_entity_id?: string | null;
  recipient_emails: string[];
  parameters: Record<string, unknown>;
  is_enabled: boolean;
  last_run_at?: unknown;
  next_run_at?: unknown;
  created_at?: unknown;
}

const JOBS_KEY = ["reports", "jobs"] as const;
const SCHEDULES_KEY = ["reports", "schedules"] as const;

export function useReportJobs() {
  return useQuery({
    queryKey: JOBS_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ items: ReportJob[]; total: number }>(
        "/api/v1/reports/jobs",
      );
      return data;
    },
    refetchInterval: 5_000,
  });
}

export function useReportJob(id: string | null) {
  return useQuery({
    queryKey: [...JOBS_KEY, id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<ReportJob>(`/api/v1/reports/jobs/${id}`);
      return data;
    },
    // react-query v5 — the refetchInterval callback receives the Query
    // object, not the data. Read the latest data off query.state.
    refetchInterval: (query) => {
      const i = query.state.data;
      return i && (i.status === "complete" || i.status === "failed")
        ? false
        : 3_000;
    },
  });
}

export function useGenerateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      report_type: ReportType;
      format: ReportFormat;
      parameters?: Record<string, unknown>;
      scope_entity_id?: string;
    }) => {
      const { data } = await api.post<{ job_id: string; status: string }>(
        "/api/v1/reports/generate",
        input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: JOBS_KEY }),
  });
}

export function useReportSchedules() {
  return useQuery({
    queryKey: SCHEDULES_KEY,
    queryFn: async () => {
      const { data } = await api.get<{
        items: ReportSchedule[];
        total: number;
      }>("/api/v1/reports/schedules");
      return data;
    },
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      report_type: ReportType;
      format: ReportFormat;
      frequency: ReportFrequency;
      schedule_day?: number;
      schedule_hour: number;
      scope_entity_id?: string;
      recipient_emails?: string[];
      parameters?: Record<string, unknown>;
      is_enabled?: boolean;
    }) => {
      const { data } = await api.post<ReportSchedule>(
        "/api/v1/reports/schedules",
        input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}

export function useUpdateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      patch: Partial<{
        name: string;
        format: ReportFormat;
        frequency: ReportFrequency;
        schedule_day: number;
        schedule_hour: number;
        recipient_emails: string[];
        parameters: Record<string, unknown>;
        is_enabled: boolean;
      }>;
    }) => {
      const { data } = await api.put<ReportSchedule>(
        `/api/v1/reports/schedules/${input.id}`,
        input.patch,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/reports/schedules/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  });
}

export function useRunScheduleNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ job_id: string }>(
        `/api/v1/reports/schedules/${id}/run-now`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: JOBS_KEY }),
  });
}
